package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.ArrowUpward
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.tooling.preview.PreviewScreenSizes
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.preview.IncomeVsExpensesSummaryPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.abs

@Composable
fun IncomeVsExpensesSummaryScreen(
    onNavigateBack: () -> Unit,
    onMonthClick: (String) -> Unit = {},
    viewModel: IncomeVsExpensesSummaryViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    IncomeVsExpensesSummaryScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onMonthClick = onMonthClick,
        onTimeRangeSelected = viewModel::setTimeRange,
        onRefresh = viewModel::refresh
    )
}

@Composable
fun IncomeVsExpensesSummaryScreenContent(
    state: IncomeVsExpensesSummaryScreenState,
    onNavigateBack: () -> Unit,
    onMonthClick: (String) -> Unit = {},
    onTimeRangeSelected: (IncomeVsExpensesTimeRange) -> Unit = {},
    onRefresh: () -> Unit = {}
) {
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "Income vs Expenses",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        when (state) {
            is IncomeVsExpensesSummaryScreenState.Loading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
            is IncomeVsExpensesSummaryScreenState.Error -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding)
                        .padding(24.dp),
                    contentAlignment = Alignment.Center
                ) {
                    GlassCard(modifier = Modifier.fillMaxWidth()) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Text(
                                text = "Unable to load data",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.error
                            )
                            Text(
                                text = state.message,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Button(
                                onClick = onRefresh,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary,
                                    contentColor = Color(0xFF003731)
                                )
                            ) {
                                Text("Retry")
                            }
                        }
                    }
                }
            }
            is IncomeVsExpensesSummaryScreenState.Loaded -> {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    item(key = "time_range_selector") {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            IncomeVsExpensesTimeRangeSegmentedControl(
                                selectedRange = state.selectedRange,
                                onRangeSelected = onTimeRangeSelected
                            )
                        }
                    }

                    item(key = "totals_summary_card") {
                        TotalsSummaryCard(state = state)
                    }

                    if (state.monthlyData.isNotEmpty()) {
                        item(key = "vertical_table_card") {
                            MonthlyVerticalTableCard(
                                state = state,
                                onMonthClick = onMonthClick
                            )
                        }
                    }

                    item(key = "monthly_breakdown_header") {
                        Column(modifier = Modifier.padding(top = 8.dp)) {
                            Text(
                                text = "Monthly Breakdown",
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = "Detailed cash flow and spending for each month",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }

                    if (state.monthlyData.isEmpty()) {
                        item(key = "empty_data") {
                            GlassCard(modifier = Modifier.fillMaxWidth()) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(24.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = "No income or expense data found for this period.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        }
                    } else {
                        // Present months with most recent first
                        items(state.monthlyData.reversed(), key = { it.yearMonth }) { monthItem ->
                            MonthDetailCard(
                                item = monthItem,
                                onClick = { onMonthClick(monthItem.yearMonth) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TotalsSummaryCard(
    state: IncomeVsExpensesSummaryScreenState.Loaded,
    modifier: Modifier = Modifier
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)
    val isNetPositive = state.netIncome >= 0

    GlassCard(modifier = modifier.fillMaxWidth()) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = "Period Totals",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )

            // Primary 2x2 Grid of Key Totals
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Total Income Box
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Total Income",
                    value = "+$${state.totalIncome.formatAmount()}",
                    valueColor = greenColor,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.ArrowUpward,
                            contentDescription = null,
                            tint = greenColor,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )

                // Total Expenses Box
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Total Expenses",
                    value = "-$${state.totalExpenses.formatAmount()}",
                    valueColor = redColor,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.ArrowDownward,
                            contentDescription = null,
                            tint = redColor,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Net Cash Flow Box
                val netPrefix = if (isNetPositive) "+" else "-"
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Net Cash Flow",
                    value = "$netPrefix$${abs(state.netIncome).formatAmount()}",
                    valueColor = if (isNetPositive) greenColor else redColor
                )

                // Savings Rate Box
                val ratePrefix = if (state.savingsRate >= 0) "+" else ""
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Savings Rate",
                    value = "$ratePrefix${state.savingsRate.formatAmount()}%",
                    valueColor = if (state.savingsRate >= 0) greenColor else redColor
                )
            }

            HorizontalDivider(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.08f)
            )

            // Monthly Averages
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(
                    text = "Monthly Averages",
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    AverageItem(label = "Avg. Income", amount = state.averageMonthlyIncome, color = greenColor, isExpense = false)
                    AverageItem(label = "Avg. Expense", amount = state.averageMonthlyExpenses, color = redColor, isExpense = true)
                    val netAvgSign = if (state.averageMonthlyNet >= 0) "+" else "-"
                    AverageItem(label = "Avg. Net", amount = abs(state.averageMonthlyNet), color = if (state.averageMonthlyNet >= 0) greenColor else redColor, prefix = netAvgSign)
                }
            }
        }
    }
}

@Composable
private fun MetricCard(
    label: String,
    value: String,
    valueColor: Color,
    modifier: Modifier = Modifier,
    icon: (@Composable () -> Unit)? = null
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x10FFFFFF))
            .padding(12.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                if (icon != null) {
                    icon()
                }
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = valueColor,
                maxLines = 1
            )
        }
    }
}

@Composable
private fun AverageItem(
    label: String,
    amount: Double,
    color: Color,
    isExpense: Boolean = false,
    prefix: String? = null
) {
    Column {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        val formattedSign = prefix ?: if (isExpense) "-" else "+"
        Text(
            text = "$formattedSign$${amount.formatAmount()}",
            style = MaterialTheme.typography.bodySmall,
            fontWeight = FontWeight.SemiBold,
            color = color
        )
    }
}

@Composable
private fun MonthlyVerticalTableCard(
    state: IncomeVsExpensesSummaryScreenState.Loaded,
    modifier: Modifier = Modifier,
    onMonthClick: (String) -> Unit = {}
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)

    GlassCard(modifier = modifier.fillMaxWidth()) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = "Cash Flow Table",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = "Monthly comparison of income, expenses, and savings",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                MonthlyTableRow(
                    col1 = "Month",
                    col2 = "Income",
                    col3 = "Expenses",
                    col4 = "Net",
                    isHeader = true
                )

                HorizontalDivider(
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
                )

                state.monthlyData.forEach { month ->
                    val netColor = if (month.netIncome >= 0) greenColor else redColor
                    val netSign = if (month.netIncome >= 0) "+" else "-"

                    MonthlyTableRow(
                        col1 = month.yearMonth,
                        col2 = "+$${month.income.formatAmount()}",
                        col3 = "-$${month.expenses.formatAmount()}",
                        col4 = "$netSign$${abs(month.netIncome).formatAmount()}",
                        col2Color = greenColor,
                        col3Color = redColor,
                        col4Color = netColor,
                        onClick = { onMonthClick(month.yearMonth) }
                    )
                }

                HorizontalDivider(
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
                )

                // Totals Row
                val totalNetColor = if (state.netIncome >= 0) greenColor else redColor
                val totalNetSign = if (state.netIncome >= 0) "+" else "-"

                MonthlyTableRow(
                    col1 = "Total",
                    col2 = "+$${state.totalIncome.formatAmount()}",
                    col3 = "-$${state.totalExpenses.formatAmount()}",
                    col4 = "$totalNetSign$${abs(state.netIncome).formatAmount()}",
                    isBold = true,
                    col2Color = greenColor,
                    col3Color = redColor,
                    col4Color = totalNetColor,
                    backgroundColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.06f)
                )

                // Averages Row
                val avgNetColor = if (state.averageMonthlyNet >= 0) greenColor else redColor
                val avgNetSign = if (state.averageMonthlyNet >= 0) "+" else "-"

                MonthlyTableRow(
                    col1 = "Average",
                    col2 = "+$${state.averageMonthlyIncome.formatAmount()}",
                    col3 = "-$${state.averageMonthlyExpenses.formatAmount()}",
                    col4 = "$avgNetSign$${abs(state.averageMonthlyNet).formatAmount()}",
                    isBold = true,
                    col2Color = greenColor,
                    col3Color = redColor,
                    col4Color = avgNetColor,
                    backgroundColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.03f)
                )
            }
        }
    }
}

@Composable
private fun MonthlyTableRow(
    col1: String,
    col2: String,
    col3: String,
    col4: String,
    modifier: Modifier = Modifier,
    isHeader: Boolean = false,
    isBold: Boolean = false,
    col2Color: Color? = null,
    col3Color: Color? = null,
    col4Color: Color? = null,
    backgroundColor: Color = Color.Transparent,
    onClick: (() -> Unit)? = null
) {
    val headerStyle = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold)
    val textStyle = if (isBold) {
        MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold)
    } else {
        MaterialTheme.typography.bodySmall
    }
    val defaultTextColor = if (isHeader) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface

    val clickModifier = if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier

    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .background(backgroundColor)
            .then(clickModifier)
            .padding(vertical = if (isHeader) 6.dp else 4.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = col1,
            style = if (isHeader) headerStyle else textStyle,
            color = defaultTextColor,
            modifier = Modifier.weight(1.0f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Start
        )
        Text(
            text = col2,
            style = if (isHeader) headerStyle else textStyle,
            color = col2Color ?: defaultTextColor,
            modifier = Modifier.weight(1.2f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.End
        )
        Text(
            text = col3,
            style = if (isHeader) headerStyle else textStyle,
            color = col3Color ?: defaultTextColor,
            modifier = Modifier.weight(1.2f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.End
        )
        Text(
            text = col4,
            style = if (isHeader) headerStyle else textStyle,
            color = col4Color ?: defaultTextColor,
            modifier = Modifier.weight(1.1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.End
        )
    }
}

@Composable
private fun MonthDetailCard(
    item: MonthlyIncomeVsExpenses,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)
    val isNetPositive = item.netIncome >= 0
    val netSign = if (isNetPositive) "+" else "-"

    GlassCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onClick
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Header Row: Month Name + Net Pill
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = item.yearMonth,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )

                // Net Pill
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(12.dp))
                        .background(if (isNetPositive) greenColor.copy(alpha = 0.15f) else redColor.copy(alpha = 0.15f))
                        .padding(horizontal = 10.dp, vertical = 4.dp)
                ) {
                    Text(
                        text = "$netSign$${abs(item.netIncome).formatAmount()}",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        color = if (isNetPositive) greenColor else redColor
                    )
                }
            }

            // Stats Row: Income, Expenses, Savings Rate
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column {
                    Text(
                        text = "Income",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = "+$${item.income.formatAmount()}",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold,
                        color = greenColor
                    )
                }

                Column {
                    Text(
                        text = "Expenses",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = "-$${item.expenses.formatAmount()}",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold,
                        color = redColor
                    )
                }

                Spacer(modifier = Modifier.width(1.dp))
            }

            // Expense Ratio Progress Bar
            val spendRatio = if (item.income > 0) (item.expenses / item.income).toFloat().coerceIn(0f, 1f) else 1f
            LinearProgressIndicator(
                progress = { spendRatio },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(6.dp)
                    .clip(RoundedCornerShape(3.dp)),
                color = if (spendRatio <= 0.8f) greenColor else if (spendRatio <= 1.0f) Color(0xFFFFB74D) else redColor,
                trackColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.1f)
            )

            // Top Expense Categories (if available)
            if (item.topExpenseCategories.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text(
                        text = "Top Expense Categories",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        item.topExpenseCategories.forEach { category ->
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(Color(0x10FFFFFF))
                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                            ) {
                                Text(
                                    text = "${category.category}: $${category.amount.formatAmount()}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    }
                }
            }

            // Transaction Count Info
            if (item.incomeTransactionCount > 0 || item.expenseTransactionCount > 0) {
                Text(
                    text = "${item.incomeTransactionCount} income • ${item.expenseTransactionCount} expense transactions",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
                )
            }
        }
    }
}

@Composable
fun IncomeVsExpensesTimeRangeSegmentedControl(
    selectedRange: IncomeVsExpensesTimeRange,
    onRangeSelected: (IncomeVsExpensesTimeRange) -> Unit,
    modifier: Modifier = Modifier
) {
    val options = IncomeVsExpensesTimeRange.entries.toTypedArray()
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x15FFFFFF))
            .padding(2.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        options.forEach { option ->
            val isSelected = option == selectedRange
            val backgroundColor by animateColorAsState(
                targetValue = if (isSelected) MaterialTheme.colorScheme.primary else Color.Transparent,
                label = "bgColor"
            )
            val contentColor by animateColorAsState(
                targetValue = if (isSelected) Color(0xFF003731) else MaterialTheme.colorScheme.onSurfaceVariant,
                label = "contentColor"
            )

            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(backgroundColor)
                    .clickable { onRangeSelected(option) }
                    .padding(horizontal = 12.dp, vertical = 6.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = option.displayName,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = contentColor
                )
            }
        }
    }
}

@PreviewLightDark
@PreviewScreenSizes
@Composable
fun IncomeVsExpensesSummaryScreenPreview(
    @PreviewParameter(IncomeVsExpensesSummaryPreviewParameterProvider::class) state: IncomeVsExpensesSummaryScreenState
) {
    AppTheme(darkTheme = isSystemInDarkTheme(), dynamicColor = false) {
        Surface {
            IncomeVsExpensesSummaryScreenContent(
                state = state,
                onNavigateBack = {},
                onMonthClick = {}
            )
        }
    }
}
